import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Banknote } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  useBulkSettleClaims,
  useSettlementOptions,
  type BulkSettleItem,
} from '@/admin/hooks/use-settlement'
import { toastApiError } from '@/shared/lib/api-error'

/** A claim available to settle in this batch. */
export interface BulkSettleClaim {
  uuid: string
  label: string
  ownerName?: string
}

type PayoutMode = 'combined' | 'per_claim'

/**
 * Generic bulk settlement dialog. Settles a batch of claims in one go with a
 * chosen payout mode:
 *   - combined  → one payment to a single payee (e.g. the group)
 *   - per_claim → one payment per claim to its own payee (e.g. each owner)
 *
 * Tenant-agnostic - driven by the claims passed in, not any tenant concept.
 */
export function BulkSettleDialog({
  open,
  onOpenChange,
  parentUuid,
  claims,
  defaultPayoutMode = 'combined',
  defaultCombinedPayee = '',
  settleActionKey = 'bulk_settle',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Claim the combined payment is recorded on (e.g. the umbrella claim). */
  parentUuid: string
  claims: BulkSettleClaim[]
  defaultPayoutMode?: PayoutMode
  defaultCombinedPayee?: string
  settleActionKey?: string
}) {
  const { t } = useTranslation()
  const bulkSettle = useBulkSettleClaims()
  const { data: options } = useSettlementOptions()
  const paymentTypes = options?.payment_types ?? []

  const [payoutMode, setPayoutMode] = useState<PayoutMode>(defaultPayoutMode)
  const [paymentType, setPaymentType] = useState('')
  const [reference, setReference] = useState('')
  const [combinedPayee, setCombinedPayee] = useState(defaultCombinedPayee)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(claims.map((c) => c.uuid)),
  )
  const [amounts, setAmounts] = useState<Record<string, string>>({})

  const selectedClaims = useMemo(
    () => claims.filter((c) => selected.has(c.uuid)),
    [claims, selected],
  )
  const total = useMemo(
    () =>
      selectedClaims.reduce(
        (sum, c) => sum + (parseFloat(amounts[c.uuid] || '0') || 0),
        0,
      ),
    [selectedClaims, amounts],
  )

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })

  const handleSubmit = async () => {
    if (selectedClaims.length === 0) {
      toast.error('Select at least one claim to settle.')
      return
    }
    if (!paymentType) {
      toast.error('Choose a payment type.')
      return
    }
    const items: BulkSettleItem[] = selectedClaims.map((c) => ({
      claim_uuid: c.uuid,
      amount: amounts[c.uuid] || '0',
      ...(payoutMode === 'per_claim'
        ? { recipient_type: 'HOMEOWNER', payee: c.ownerName || '' }
        : {}),
    }))
    try {
      const res = await bulkSettle.mutateAsync({
        payout_mode: payoutMode,
        payment_type: paymentType,
        reference_number: reference,
        settle_action_key: settleActionKey,
        items,
        ...(payoutMode === 'combined'
          ? {
              combined_on_claim_uuid: parentUuid,
              combined_recipient_type: 'COMMUNITY',
              combined_payee: combinedPayee,
            }
          : {}),
      })
      toast.success(
        `${res.payments_created} payment${res.payments_created !== 1 ? 's' : ''} created` +
          ` · ${res.claims_settled} claim${res.claims_settled !== 1 ? 's' : ''} settled`,
      )
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, 'Failed to settle claims')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Bulk Settle Claims
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Settle the selected claims together. Choose one combined payment or
            a payment to each owner.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Payout mode + payment details */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Payout
              </Label>
              <Select
                value={payoutMode}
                onValueChange={(v) => setPayoutMode(v as PayoutMode)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="combined">
                    One combined payment to the group
                  </SelectItem>
                  <SelectItem value="per_claim">
                    Pay each owner individually
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Payment Type
              </Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('settlement.bulkSettle.selectPlaceholder', 'Select…')} />
                </SelectTrigger>
                <SelectContent>
                  {paymentTypes.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>
                      {pt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Reference
              </Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('settlement.bulkSettle.referencePlaceholder', 'Cheque / ACH reference')}
              />
            </div>
            {payoutMode === 'combined' && (
              <div className="min-w-0">
                <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Pay to (group)
                </Label>
                <Input
                  value={combinedPayee}
                  onChange={(e) => setCombinedPayee(e.target.value)}
                  placeholder={t('settlement.bulkSettle.groupNamePlaceholder', '{{groupNoun}} name')}
                />
              </div>
            )}
          </div>

          {/* Claims + amounts */}
          <div>
            <Label className="mb-2 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Claims ({selectedClaims.length}/{claims.length})
            </Label>
            <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
              <div className="grid grid-cols-[auto_1.6fr_1fr] items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
                <span />
                <span>Claim</span>
                <span>Amount</span>
              </div>
              <div className="clara-scroll max-h-[240px] overflow-y-auto">
                {claims.map((c) => (
                  <div
                    key={c.uuid}
                    className="grid grid-cols-[auto_1.6fr_1fr] items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-3 py-2 last:border-b-0"
                  >
                    <Checkbox
                      checked={selected.has(c.uuid)}
                      onCheckedChange={() => toggle(c.uuid)}
                    />
                    <span className="flex flex-col">
                      <span className="text-[12px] text-[var(--ink)]">
                        {c.label}
                      </span>
                      {c.ownerName && (
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {c.ownerName}
                        </span>
                      )}
                    </span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={amounts[c.uuid] ?? ''}
                      onChange={(e) =>
                        setAmounts((prev) => ({
                          ...prev,
                          [c.uuid]: e.target.value,
                        }))
                      }
                      placeholder="0.00"
                      className="h-8"
                    />
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-2 text-right text-[12px] text-[var(--ink-2)]">
              Total: <span className="font-medium">{total.toFixed(2)}</span>
              {payoutMode === 'combined'
                ? ' - one payment to the group'
                : ` - ${selectedClaims.length} owner payment${selectedClaims.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={bulkSettle.isPending || selectedClaims.length === 0}
            className="cursor-pointer gap-1.5"
          >
            {bulkSettle.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Banknote className="h-3.5 w-3.5" />
            )}
            Settle {selectedClaims.length} claim
            {selectedClaims.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
